import type {
  GardenZone,
  PlantPlacement,
  VerticalSupport,
} from "@/types/garden";
import {
  DEFAULT_GARDEN_HEIGHT_CM,
  DEFAULT_GARDEN_WIDTH_CM,
} from "@/lib/constants";

/**
 * Guest mode — drawing a garden without an account.
 *
 * The plan lives in localStorage until the visitor signs up, at which point
 * `migrateGuestGarden` pushes it to the API. Everything that knows the storage
 * shape lives here so the editor, the signup page and the entry route can't
 * drift apart.
 */

export const GUEST_GARDEN_KEY = "guest_garden";

/**
 * A cookie rather than sessionStorage: the middleware runs on the server and
 * cannot read web storage, and sessionStorage died with the tab while the
 * garden itself (localStorage) survived — leaving people with a saved plan
 * they could no longer reach.
 *
 * This cookie grants no data access. It only lets the middleware render the
 * editor shell; every API route still requires a real session, so a forged
 * cookie buys an empty page and nothing else.
 */
export const GUEST_MODE_COOKIE = "guest_mode";
const COOKIE_MAX_AGE_DAYS = 30;

export type GuestGarden = {
  id: string;
  name: string;
  season_year: number;
  orientation: string;
  width_cm: number;
  height_cm: number;
  zones: GardenZone[];
  placements: PlantPlacement[];
  supports?: VerticalSupport[];
  /** Set by the onboarding; copied to the profile at signup. */
  department?: string | null;
};

export function isGuestCookieSet(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split("; ")
    .some((part) => part.startsWith(`${GUEST_MODE_COOKIE}=`));
}

export function setGuestCookie(): void {
  if (typeof document === "undefined") return;
  const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${GUEST_MODE_COOKIE}=1; path=/; max-age=${maxAge}; SameSite=Lax`;
}

export function clearGuestCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${GUEST_MODE_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
}

export function readGuestGarden(): GuestGarden | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(GUEST_GARDEN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GuestGarden;
    return parsed && typeof parsed.id === "string" ? parsed : null;
  } catch {
    // Corrupted payload: drop it rather than trapping the visitor on a
    // broken editor with no way to start over.
    localStorage.removeItem(GUEST_GARDEN_KEY);
    return null;
  }
}

export function writeGuestGarden(garden: GuestGarden): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(GUEST_GARDEN_KEY, JSON.stringify(garden));
}

export function clearGuestGarden(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(GUEST_GARDEN_KEY);
}

/** A ready-to-edit plan, so arriving from a guide page skips the empty form. */
export function createStarterGarden(): GuestGarden {
  return {
    id: crypto.randomUUID(),
    name: "Mon potager",
    season_year: new Date().getFullYear(),
    orientation: "N",
    width_cm: DEFAULT_GARDEN_WIDTH_CM,
    height_cm: DEFAULT_GARDEN_HEIGHT_CM,
    zones: [],
    placements: [],
    supports: [],
  };
}

export type MigrationResult =
  | { status: "migrated"; gardenId: string }
  | { status: "nothing" }
  | { status: "failed" };

/**
 * Moves the local plan into the freshly created account.
 *
 * Called right after signup, while the session cookie is already set. The
 * local copy is only cleared once the server has accepted both the garden and
 * its layout — a failed migration leaves the work in the browser rather than
 * destroying it.
 */
export async function migrateGuestGarden(): Promise<MigrationResult> {
  const guest = readGuestGarden();
  if (!guest) return { status: "nothing" };

  try {
    const createRes = await fetch("/api/gardens", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: guest.name,
        season_year: guest.season_year,
        orientation: guest.orientation,
        width_cm: guest.width_cm,
        height_cm: guest.height_cm,
        supports: guest.supports ?? [],
      }),
    });
    if (!createRes.ok) return { status: "failed" };

    const created = (await createRes.json()) as { id?: string };
    if (!created.id) return { status: "failed" };

    const hasLayout =
      (guest.zones?.length ?? 0) > 0 || (guest.placements?.length ?? 0) > 0;

    if (hasLayout) {
      const layoutRes = await fetch(`/api/gardens/${created.id}/layout`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          zones: guest.zones ?? [],
          placements: guest.placements ?? [],
          supports: guest.supports ?? [],
        }),
      });
      if (!layoutRes.ok) return { status: "failed" };
    }

    if (guest.department) {
      // Best effort: a failed profile update must not lose the garden.
      await fetch("/api/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ zone_fr: guest.department }),
      }).catch(() => {});
    }

    clearGuestGarden();
    clearGuestCookie();
    return { status: "migrated", gardenId: created.id };
  } catch {
    return { status: "failed" };
  }
}
